import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { SplitPaneSection } from '@/components/bespoke/split-pane';

/**
 * AppShell-mounted right-rail registry. Pages register sections via
 * `usePagePaneSections(...)`; the shell prepends them in front of the
 * always-present `stats` + `status` defaults. A page overrides the default
 * stats body by registering a section with `id === PANE_SECTION.stats`.
 */

export const PANE_SECTION = {
  status: 'status',
  stats: 'stats',
  threads: 'threads',
  evidence: 'evidence',
  /**
   * Surface-anchored multi-turn chat. Mounted on dossier (subject = the
   * entity), graph (subject = the current view), and any page where the
   * analyst wants to discuss what's on screen. Replaces the v0.2 single-
   * turn `graph-ask` section.
   */
  chat: 'chat',
  nodeDetail: 'node-detail',
  evidenceDetail: 'evidence-detail',
  candidateDetail: 'er-candidate-detail',
  auditDetail: 'audit-detail',
} as const;

export type PaneSectionId = (typeof PANE_SECTION)[keyof typeof PANE_SECTION];

interface PagePaneSectionsContextValue {
  pageSections: SplitPaneSection[];
  setPageSections: (sections: SplitPaneSection[]) => void;
}

const PagePaneSectionsContext =
  createContext<PagePaneSectionsContextValue | null>(null);

function sameSections(a: SplitPaneSection[], b: SplitPaneSection[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export function PagePaneSectionsProvider({ children }: { children: ReactNode }) {
  // Identity-guarded setter: navigating to a page with no sections after a
  // page that had some skips a re-render of the shell when both lists are
  // already empty (and other no-op cases).
  const [value, setValue] = useState<PagePaneSectionsContextValue>(() => ({
    pageSections: [],
    setPageSections: (next) =>
      setValue((prev) =>
        sameSections(prev.pageSections, next)
          ? prev
          : { ...prev, pageSections: next },
      ),
  }));
  return (
    <PagePaneSectionsContext.Provider value={value}>
      {children}
    </PagePaneSectionsContext.Provider>
  );
}

/** AppShell-side reader. Pages call `usePagePaneSections` instead. */
export function usePagePaneSectionsValue(): SplitPaneSection[] {
  return useContext(PagePaneSectionsContext)?.pageSections ?? [];
}

/**
 * Page-side hook: register sections in the AppShell rail. The provided list
 * replaces any previously-registered list when `deps` change, and clears on
 * unmount. `useLayoutEffect` (not `useEffect`) so registration commits before
 * paint — otherwise pages flash to [Stats, Status]-only on every nav.
 */
export function usePagePaneSections(
  sections: SplitPaneSection[],
  deps: DependencyList,
): void {
  const ctx = useContext(PagePaneSectionsContext);
  const latest = useRef(sections);
  latest.current = sections;

  useLayoutEffect(() => {
    if (!ctx) return;
    ctx.setPageSections(latest.current);
    return () => ctx.setPageSections([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Conditionally register a single "detail" section that's only present when
 * `item` is non-null — the shape every selection-driven page in the rail
 * uses (graph node, investigation evidence, ER candidate, audit row).
 */
export function useDetailPaneSection<T>(
  item: T | null,
  config: {
    id: PaneSectionId;
    title: string;
    icon: ReactNode;
    render: (item: T) => ReactNode;
  },
  deps: DependencyList,
): void {
  usePagePaneSections(
    item
      ? [
          {
            id: config.id,
            title: config.title,
            icon: config.icon,
            content: (
              <ScrollArea className="h-full">{config.render(item)}</ScrollArea>
            ),
          },
        ]
      : [],
    deps,
  );
}
